export interface Modality {
  id: string
  name: string
  short: string
  detail: string
  products: string[]
  ctaLabel?: string
  image?: string
  imageAlt?: string
  imageFit?: 'contain' | 'cover'
}

export const MODALITY_IMAGES = {
  laser:
    'https://cdn.magicpatterns.com/patterns/generated-images/cdc905b5-7fdf-466e-a3e5-f2c20f0ae8ef.jpg',
  shockwave:
    'https://cdn.magicpatterns.com/patterns/generated-images/ab75f2f5-a243-4ac6-8994-cc4558777d81.jpg',
  led: 'https://cdn.magicpatterns.com/patterns/generated-images/3fba62ac-6e3b-478f-82c4-1da798b9f9c8.jpg',
  thermal:
    'https://cdn.magicpatterns.com/patterns/generated-images/1f5a0d19-d7df-43a0-a0aa-aaf2f489e6a9.jpg',
}

export const CATEGORY_IMAGES = {
  laser:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699f58ab8a039e9fc4bf5fb4.webp',
  shockwave:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699cfefd5dfd506dd39bffa7.png',
  heit:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/6982c2352dd9851edee6e2d3.webp',
  led: 'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699cff7d8ea4c98451074f30.png',
  thermal:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/6982c1c31dfc023c803236b0.webp',
  deepOscillation:
    'https://assets.cdn.filesafe.space/wZmCOMOYUn4CfsoCHQM0/media/699e90c59810935be30b7732.png',
}

export const orbitNodes: {
  id: string
  name: string
  detail: string
}[] = [
  {
    id: 'class-iv-laser',
    name: 'Class IV Laser',
    detail: 'Laser therapy systems for modern practices.',
  },
  {
    id: 'shockwave',
    name: 'Shockwave',
    detail: 'Focused and radial options to compare with JAG.',
  },
  {
    id: 'heit',
    name: 'High Energy Inductive Therapy',
    detail: 'A modality category to evaluate for your practice.',
  },
  {
    id: 'led',
    name: 'Whole Body Red Light / LED Therapy',
    detail: 'Whole-body and targeted light therapy options.',
  },
  {
    id: 'thermal',
    name: 'Thermal Imaging',
    detail: 'Thermal imaging options for assessment workflows.',
  },
  {
    id: 'deep-oscillation',
    name: 'Deep Oscillation',
    detail: 'Deep oscillation technology for practice protocols.',
  },
  {
    id: 'used-equipment',
    name: 'Used Equipment',
    detail: 'Pre-owned and refurbished modality sourcing.',
  },
  {
    id: 'concierge',
    name: 'Concierge Modality Suite',
    detail: 'A curated system built around your doctor needs.',
  },
  {
    id: 'neuro-reformer',
    name: 'Neuro Reformer',
    detail: 'Guided movement technology for rehab and functional recovery workflows.',
  },
  {
    id: 'regenmedix',
    name: 'RegenMedix',
    detail: 'Recovery-focused technology options to evaluate with JAG.',
  },
]

export const categories: Modality[] = [
  {
    id: 'class-iv-laser',
    name: 'Class IV Laser',
    short: 'Laser systems for modern practices',
    detail:
      'Compare laser options with practical guidance on fit, team training, and implementation.',
    products: [
      'Endolaser HP',
      'Aspen Summit',
      'Aspen Ascent Compact',
      'MRM Alpha',
    ],
    image: CATEGORY_IMAGES.laser,
  },
  {
    id: 'shockwave',
    name: 'Shockwave',
    short: 'Focused and radial options',
    detail:
      'Understand focused and radial shockwave options before choosing the right direction for your office.',
    products: [
      'Focused Shockwave Endopuls FSWT',
      'Radial Shockwave Endopuls 811',
      'AWUSA Radial Shockwave',
      'AWUSA Dual-Radial Shockwave',
    ],
    image: CATEGORY_IMAGES.shockwave,
  },
  {
    id: 'heit',
    name: 'High Energy Inductive Therapy',
    short: 'A technology category to evaluate',
    detail:
      'A clear conversation about whether this modality belongs in your practice and how it would be implemented.',
    products: ['High Energy Inductive Therapy'],
    image: CATEGORY_IMAGES.heit,
  },
  {
    id: 'led',
    name: 'Whole Body Red Light / LED Therapy',
    short: 'Whole-body and targeted light therapy',
    detail:
      'Review LED therapy systems with a practical view of patient fit, staff workflow, and implementation.',
    products: [
      'LumaNova LumaPod',
      'LumaNova LumaBag',
      'TheraLight 360i',
      'TheraLight FIT',
      'TheraLight 360',
    ],
    image: CATEGORY_IMAGES.led,
  },
  {
    id: 'thermal',
    name: 'Thermal Imaging',
    short: 'Thermal imaging options',
    detail:
      'Explore imaging options and how they can support your assessment workflow.',
    products: ['ThermoVision T2', 'WellVu Medical', 'WellVu Veterinary'],
    image: CATEGORY_IMAGES.thermal,
  },
  {
    id: 'deep-oscillation',
    name: 'Deep Oscillation',
    short: 'Hivamat technology',
    detail:
      'A focused modality conversation for practices evaluating Hivamat technology.',
    products: ['Hivamat'],
    image: CATEGORY_IMAGES.deepOscillation,
  },
  {
    id: 'used-equipment',
    name: 'Used Equipment',
    short: 'Pre-owned and refurbished availability',
    detail:
      'Talk with JAG about sourcing and evaluating pre-owned modalities as availability changes.',
    products: ['Pre-owned and refurbished modality availability'],
    image:
      'https://asset.imagine.art/video/thumbnails/ce37a282-5aa7-44c8-b595-bad579344376',
    imageAlt: 'Used medical modality equipment in a clean clinic',
    imageFit: 'cover',
  },
  {
    id: 'concierge',
    name: 'Concierge Modality Suite',
    short: 'A curated system for doctor needs',
    detail:
      'A coordinated approach to selecting modalities that work together around your practice goals.',
    products: ['Curated modality system'],
    image:
      'https://asset.imagine.art/video/thumbnails/fe654d8a-8236-4491-a674-62fed502fdf4',
    imageAlt: 'Concierge modality planning and implementation support',
    imageFit: 'cover',
  },
  {
    id: 'neuro-reformer',
    name: 'Neuro Reformer',
    short: 'Guided movement and functional recovery workflows',
    detail:
      'Neuromuscular re-education and guided movement technology for practices evaluating rehab, coordination, balance, and functional recovery workflows.',
    products: [
      'Compare fit, training needs, patient use cases, and implementation requirements before choosing a system.',
    ],
    ctaLabel: 'Call About Neuro Reformer',
    image:
      'https://asset.imagine.art/video/thumbnails/193c7ae9-9406-44b8-8ca5-11c1498093a1',
    imageAlt: 'Neuro Reformer neuromuscular rehabilitation setup',
    imageFit: 'cover',
  },
  {
    id: 'regenmedix',
    name: 'RegenMedix',
    short: 'Recovery-focused technology options',
    detail:
      'Regenerative medicine and recovery-focused technology options for practices evaluating advanced patient care, wellness, and recovery protocols.',
    products: [
      'Talk with JAG about clinical fit, compliance considerations, vendor options, and how the category may support your practice goals.',
    ],
    ctaLabel: 'Call About RegenMedix',
    image:
      'https://asset.imagine.art/video/thumbnails/91e8bdc8-ade9-466b-a52b-873d4b6ef87d',
    imageAlt: 'Regenerative medicine training and support supplies',
    imageFit: 'cover',
  },
]

export const conciergeModalities = [
  {
    name: 'Class IV Laser',
    desc: 'Compare power, footprint, training, and patient-use fit before choosing a system.',
  },
  {
    name: 'Shockwave',
    desc: 'Review focused and radial options with protocol and workflow context.',
  },
  {
    name: 'High Energy Inductive Therapy',
    desc: 'Evaluate whether the modality fits your treatment mix and space.',
  },
  {
    name: 'LED Light Therapy',
    desc: 'Plan around patient flow, placement, and staff use before purchase.',
  },
  {
    name: 'Thermal Imaging',
    desc: 'Understand where imaging supports assessment and communication.',
  },
  {
    name: 'Deep Oscillation',
    desc: 'Consider where it complements existing care and modality planning.',
  },
]

export const manufacturerBrands = [
  'Aspen Laser',
  'Multi Radiance Medical',
  'AWUSA',
  'LumaNova',
  'TheraLight',
  'ThermoVision',
  'WellVu',
  'Hivamat',
  'Endopuls',
]

export const CONTACT = {
  name: 'Jeff Gavette',
  phone: '(941) 441-6131',
  phoneHref: 'tel:+19414416131',
  email: 'info@jagmedicalsolutions.com',
  address: '8499 S Tamiami Trail, #255, Sarasota, FL 34238',
}
